import { sendSentryBreadcrumb } from '@/app/_lib/sentry';
import { getItem, removeItem, setItem as setLocalStorageItem } from '@/lib/storage';

const CitiboxDataKeys = {
  dropoffId: 'dropoffId',
  retrievalId: 'retrievalId',
  tracking: 'tracking',
  expeditionId: 'expeditionId',
  name: 'name',
  surname: 'surname',
  email: 'email',
  password: 'password',
  transactionId: 'transactionId',
  boxId: 'boxId',
  boxNumber: 'boxNumber',
  lockQr: 'lockQr',
  openingCode: 'openingCode',
  openingCodePickupId: 'openingCodePickupId',
  locationSecondaryId: 'locationSecondaryId',
  userGuestAccessToken: 'userGuestAccessToken',
  userRegistered: 'userRegistered',
  userPhone: 'userPhone',
  userId: 'userId',
  accessToken: 'accessToken',
  recipientPhone: 'recipientPhone',
  recipientHash: 'recipientHash',
  recipientRemoved: 'recipientRemoved',
  deliveryId: 'deliveryId',
  citiboxId: 'citiboxId',
  ble: 'ble',
  codeUsed: 'codeUsed',
  carrierId: 'carrierId',
  locationId: 'locationId',
  locationAddress: 'locationAddress',
  pickupId: 'pickupId',
  doors: 'doors',
  door: 'door',
  openingCodesCount: 'openingCodesCount',
  cameraPermissionDeniedAgain: 'cameraPermissionDeniedAgain',
  bleStatus: 'bleStatus',
  characteristicsSDK: 'characteristicsSDK',
  sessionToken: 'sessionToken',
  carrierLabel: 'carrierLabel',
  openControlLetterIncident: 'openControlLetterIncident'
};

const SDKConfigKeys = [CitiboxDataKeys.bleStatus, CitiboxDataKeys.characteristicsSDK];

const resetData = (keepSDKData?: boolean): void => {
  const preservedValues: Record<string, string> = {};

  if (keepSDKData === true) {
    SDKConfigKeys.forEach((key: string) => {
      const value = getItem(key);
      if (value !== null) {
        preservedValues[key] = value;
      }
    });
  }

  Object.entries(CitiboxDataKeys).forEach(([key]) => {
    removeItem(key);
  });

  Object.entries(preservedValues).forEach(([key, value]) => {
    setItem(key, value);
  });
};

const setExpeditionId = (expeditionId?: string): void => {
  setItem(CitiboxDataKeys.expeditionId, expeditionId);
};

const getExpeditionId = (): string | null => {
  return getItem(CitiboxDataKeys.expeditionId);
};

const setDropoffId = (dropoffId?: string): void => {
  setItem(CitiboxDataKeys.dropoffId, dropoffId);
};

const getDropoffId = (): string | null => {
  return getItem(CitiboxDataKeys.dropoffId);
};

const setTracking = (tracking?: string): void => {
  setItem(CitiboxDataKeys.tracking, tracking);
};

const getTracking = (): string | null => {
  return getItem(CitiboxDataKeys.tracking);
};

const setName = (name?: string): void => {
  setItem(CitiboxDataKeys.name, name);
};

const getName = (): string | null => {
  return getItem(CitiboxDataKeys.name);
};

const setSurname = (surname?: string): void => {
  setItem(CitiboxDataKeys.surname, surname);
};

const getSurname = (): string | null => {
  return getItem(CitiboxDataKeys.surname);
};

const setEmail = (email?: string): void => {
  setItem(CitiboxDataKeys.email, email);
};

const getEmail = (): string | null => {
  return getItem(CitiboxDataKeys.email);
};

const setPassword = (password?: string): void => {
  setItem(CitiboxDataKeys.password, password);
};

const getPassword = (): string | null => {
  return getItem(CitiboxDataKeys.password);
};

const setTransactionId = (transactionId?: string): void => {
  setItem(CitiboxDataKeys.transactionId, transactionId);
};

const getTransactionId = (defaultValue?: string): string | null => {
  return getItem(CitiboxDataKeys.transactionId) || defaultValue || null;
};

const setBoxNumber = (boxNumber?: number | null): void => {
  setItem(CitiboxDataKeys.boxNumber, boxNumber);
};

const getBoxNumber = (defaultValue?: number): number | null => {
  const value = getItem(CitiboxDataKeys.boxNumber);
  return value ? Number(value) : defaultValue || null;
};

const setLockQr = (lockQr?: string): void => {
  setItem(CitiboxDataKeys.lockQr, lockQr);
};

const getLockQr = (): string | null => {
  return getItem(CitiboxDataKeys.lockQr);
};

const setBoxId = (boxId?: string): void => {
  setItem(CitiboxDataKeys.boxId, boxId);
};

const getBoxId = (): string | null => {
  return getItem(CitiboxDataKeys.boxId);
};

const setOpeningCode = (openingCode?: string): void => {
  setItem(CitiboxDataKeys.openingCode, openingCode);
  // A code written without its pickup is not bound to any pickup, whatever was bound before (CBX-11725).
  removeItem(CitiboxDataKeys.openingCodePickupId);
};

const getOpeningCode = (defaultValue?: string): string | null => {
  return getItem(CitiboxDataKeys.openingCode) || defaultValue || null;
};

// openingCode is shared with the retrieval, dropoff and BLE flows, which read it as a plain string, so
// the pickup it belongs to is kept in a key of its own. Reusing a code is only safe for the pickup that
// generated it: a code read for any other pickup_id would open the wrong attempt (CBX-11725).
const setOpeningCodeForPickup = (pickupId: string, openingCode: string): void => {
  setItem(CitiboxDataKeys.openingCode, openingCode);
  setItem(CitiboxDataKeys.openingCodePickupId, pickupId);
};

const getOpeningCodeForPickup = (pickupId: string): string | null => {
  if (!pickupId || getItem(CitiboxDataKeys.openingCodePickupId) !== pickupId) {
    return null;
  }
  return getItem(CitiboxDataKeys.openingCode) || null;
};

// Drops the binding between the stored opening code and its pickup, so the next "Confirmar" asks
// app-service again instead of showing a code of a pickup that has since been failed (CBX-11756).
const forgetOpeningCodeForPickup = (pickupId: string): void => {
  if (pickupId && getItem(CitiboxDataKeys.openingCodePickupId) === pickupId) {
    removeItem(CitiboxDataKeys.openingCodePickupId);
  }
};

const setLocationSecondaryId = (locationSecondaryId?: string): void => {
  setItem(CitiboxDataKeys.locationSecondaryId, locationSecondaryId);
};

const getLocationSecondaryId = (): string | null => {
  return getItem(CitiboxDataKeys.locationSecondaryId);
};

const setUserPhone = (phone?: string | null): void => {
  setItem(CitiboxDataKeys.userPhone, phone);
};

const getUserPhone = (): string | null => {
  return getItem(CitiboxDataKeys.userPhone);
};

const setUserId = (userId?: string): void => {
  setItem(CitiboxDataKeys.userId, userId);
};

const getUserId = (): string | null => {
  return getItem(CitiboxDataKeys.userId);
};

const setUserGuestAccessToken = (token?: string): void => {
  setItem(CitiboxDataKeys.userGuestAccessToken, token);
};

const getUserGuestAccessToken = (): string | null => {
  return getItem(CitiboxDataKeys.userGuestAccessToken);
};

const setUserRegistered = (registered?: boolean): void => {
  setItem(CitiboxDataKeys.userRegistered, registered);
};

const getUserRegistered = (): boolean => {
  return getItem(CitiboxDataKeys.userRegistered) === 'true';
};

// A recipient whose Citibox account was removed comes back unregistered, but must not be offered a signup
// at the end of the pickup (CBX-11891). Nothing stored reads as not removed, so a pickup from an older Citibox
// version behaves as before.
const setRecipientRemoved = (removed: boolean): void => {
  setItem(CitiboxDataKeys.recipientRemoved, removed);
};

const getRecipientRemoved = (): boolean => {
  return getItem(CitiboxDataKeys.recipientRemoved) === 'true';
};

const setAccessToken = (accessToken?: string): void => {
  setItem(CitiboxDataKeys.accessToken, accessToken);
};

const getAccessToken = (): string | null => {
  return getItem(CitiboxDataKeys.accessToken);
};

const setRecipientPhone = (recipientPhone?: string | null): void => {
  setItem(CitiboxDataKeys.recipientPhone, recipientPhone);
};

const getRecipientPhone = (): string | null => {
  return getItem(CitiboxDataKeys.recipientPhone);
};

const setRecipientHash = (recipientHash?: string): void => {
  setItem(CitiboxDataKeys.recipientHash, recipientHash);
};

const getRecipientHash = (): string | null => {
  return getItem(CitiboxDataKeys.recipientHash);
};

const setDeliveryId = (deliveryId?: string): void => {
  setItem(CitiboxDataKeys.deliveryId, deliveryId);
};

const getDeliveryId = (defaultValue?: string): string | null => {
  return getItem(CitiboxDataKeys.deliveryId) || defaultValue || null;
};

const setCitiboxId = (citiboxId?: string): void => {
  setItem(CitiboxDataKeys.citiboxId, citiboxId);
};

const getCitiboxId = (): string | null => {
  return getItem(CitiboxDataKeys.citiboxId);
};

const setBle = (ble?: object): void => {
  setItem(CitiboxDataKeys.ble, ble);
};

const getBle = (defaultValue?: object): object | null => {
  const value = getItem(CitiboxDataKeys.ble);
  return value ? JSON.parse(value) : (defaultValue ?? null);
};

const setCodeUsed = (codeUsed?: boolean): void => {
  setItem(CitiboxDataKeys.codeUsed, codeUsed ? 'true' : 'false');
};

const getCodeUsed = (): boolean | null => {
  return getItem(CitiboxDataKeys.codeUsed) === 'true';
};

const setCarrierId = (carrierId?: string): void => {
  setItem(CitiboxDataKeys.carrierId, carrierId);
};

const getCarrierId = (): string | null => {
  return getItem(CitiboxDataKeys.carrierId);
};

const setLocationId = (locationId?: string): void => {
  setItem(CitiboxDataKeys.locationId, locationId);
};

const getLocationId = (): string | null => {
  return getItem(CitiboxDataKeys.locationId);
};

const setLocationAddress = (locationAddress?: string): void => {
  setItem(CitiboxDataKeys.locationAddress, locationAddress);
};

const getLocationAddress = (): string | null => {
  return getItem(CitiboxDataKeys.locationAddress);
};

const setPickupId = (pickupId?: string): void => {
  setItem(CitiboxDataKeys.pickupId, pickupId);
};

const getPickupId = (): string | null => {
  return getItem(CitiboxDataKeys.pickupId);
};

const setDoors = (doors?: object): void => {
  setItem(CitiboxDataKeys.doors, doors);
};

const getDoors = (): object | null => {
  const value = getItem(CitiboxDataKeys.doors);
  return value ? JSON.parse(value) : null;
};

const setDoor = (door?: object): void => {
  setItem(CitiboxDataKeys.door, door);
};

const getDoor = (): object | null => {
  const value = getItem(CitiboxDataKeys.door);
  return value ? JSON.parse(value) : null;
};

const setOpeningCodesCount = (attempts?: number): void => {
  setItem(CitiboxDataKeys.openingCodesCount, attempts);
};

const getOpeningCodesCount = (defaultValue?: number): number | null => {
  const value = getItem(CitiboxDataKeys.openingCodesCount);
  return value ? Number(value) : defaultValue || null;
};

const setCameraPermissionDeniedAgain = (cameraPermissionDeniedAgain?: boolean): void => {
  setItem(CitiboxDataKeys.cameraPermissionDeniedAgain, cameraPermissionDeniedAgain);
};

const getCameraPermissionDeniedAgain = (): boolean | null => {
  return getItem(CitiboxDataKeys.cameraPermissionDeniedAgain) === 'true';
};

const setRetrievalId = (retrievalId?: string): void => {
  setItem(CitiboxDataKeys.retrievalId, retrievalId);
};

const getRetrievalId = (): string | null => {
  return getItem(CitiboxDataKeys.retrievalId);
};

const setSessionToken = (sessionToken?: string): void => {
  setItem(CitiboxDataKeys.sessionToken, sessionToken);
};

const getSessionToken = (): string | null => {
  return getItem(CitiboxDataKeys.sessionToken);
};

const setCarrierLabel = (carrierLabel?: string): void => {
  setItem(CitiboxDataKeys.carrierLabel, carrierLabel);
};

const getCarrierLabel = (): string | null => {
  return getItem(CitiboxDataKeys.carrierLabel);
};

// The delivery has an incident opened from this webapp's control-letter screen that Customer Care has
// not closed yet. app-service lets the user keep trying the letter and the code, but a second wrong
// letter must not open another incident: it shows the active-incident screen instead (CBX-11756).
const setOpenControlLetterIncident = (openControlLetterIncident: boolean): void => {
  setItem(CitiboxDataKeys.openControlLetterIncident, openControlLetterIncident);
};

const getOpenControlLetterIncident = (): boolean => {
  return getItem(CitiboxDataKeys.openControlLetterIncident) === 'true';
};

export const citiboxData = {
  resetData,
  setDropoffId,
  getDropoffId,
  setTracking,
  getTracking,
  setExpeditionId,
  getExpeditionId,
  setName,
  getName,
  setSurname,
  getSurname,
  setEmail,
  getEmail,
  setPassword,
  getPassword,
  setTransactionId,
  getTransactionId,
  setBoxNumber,
  getBoxNumber,
  setLockQr,
  getLockQr,
  setBoxId,
  getBoxId,
  setOpeningCode,
  getOpeningCode,
  setOpeningCodeForPickup,
  getOpeningCodeForPickup,
  forgetOpeningCodeForPickup,
  setLocationSecondaryId,
  getLocationSecondaryId,
  setUserGuestAccessToken,
  getUserGuestAccessToken,
  setUserPhone,
  getUserPhone,
  setUserId,
  getUserId,
  setUserRegistered,
  getUserRegistered,
  setRecipientRemoved,
  getRecipientRemoved,
  setAccessToken,
  getAccessToken,
  setRecipientPhone,
  getRecipientPhone,
  setRecipientHash,
  getRecipientHash,
  setDeliveryId,
  getDeliveryId,
  setCitiboxId,
  getCitiboxId,
  setBle,
  getBle,
  setCodeUsed,
  getCodeUsed,
  getCarrierId,
  setCarrierId,
  getLocationId,
  setLocationId,
  getLocationAddress,
  setLocationAddress,
  setPickupId,
  getPickupId,
  setDoors,
  getDoors,
  setDoor,
  getDoor,
  setOpeningCodesCount,
  getOpeningCodesCount,
  setCameraPermissionDeniedAgain,
  getCameraPermissionDeniedAgain,
  setRetrievalId,
  getRetrievalId,
  setSessionToken,
  getSessionToken,
  setCarrierLabel,
  getCarrierLabel,
  setOpenControlLetterIncident,
  getOpenControlLetterIncident
};

export const useCitiboxData = () => {
  return citiboxData;
};

// Helpers

const setItem = (key: string, value?: boolean | string | number | object | null | undefined): void => {
  if (value !== null && value !== undefined) {
    const strValue = typeof value === 'object' ? JSON.stringify(value) : value.toString();
    setLocalStorageItem(key, strValue);
    if (getItem(key) !== strValue) {
      sendSentryBreadcrumb('useCitiboxData', 'setItem', { key, value }, 'info');
    }
  }
};
